import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { userService } from '../../services/userService';
import { FaPlus, FaEdit, FaTrash, FaKey, FaTimes, FaUser, FaEnvelope, FaLock, FaShieldAlt, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import './UserManagement.css';

const UserManagement = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
    isActive: true
  });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await userService.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'admin',
      isActive: true
    });
    setFormError('');
    setFormSuccess('');
    setShowForm(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email,
      role: user.role,
      isActive: user.isActive,
      password: ''
    });
    setFormError('');
    setFormSuccess('');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    setSubmitting(true);

    try {
      if (editingUser) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          isActive: formData.isActive
        };
        await userService.updateUser(editingUser._id, updateData);
        setFormSuccess('User updated successfully!');
      } else {
        // Validate password for new user
        if (!formData.password || formData.password.length < 6) {
          setFormError('Password must be at least 6 characters long');
          setSubmitting(false);
          return;
        }

        await userService.createUser(formData);
        setFormSuccess('User created successfully!');
      }

      // Close modal after a short delay
      setTimeout(() => {
        setShowForm(false);
        fetchUsers();
      }, 1500);
    } catch (error) {
      console.error('Error saving user:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save user';
      setFormError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await userService.deleteUser(id);
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Failed to delete user');
      }
    }
  };

  const handleResetPassword = async (id) => {
    const newPassword = prompt('Enter new password for user:');
    if (newPassword) {
      try {
        await userService.resetPassword(id, newPassword);
        alert('Password reset successfully');
      } catch (error) {
        console.error('Error resetting password:', error);
        alert('Failed to reset password');
      }
    }
  };

  return (
    <div className="user-management">
      <div className="management-header">
        <h1>{t('manage_users')}</h1>
        <button className="btn btn-primary" onClick={handleAddUser}>
          <FaPlus /> Add User
        </button>
      </div>

      {loading ? (
        <div className="loading">{t('loading')}</div>
      ) : (
        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user._id}>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge ${user.role}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {user.lastLogin
                      ? new Date(user.lastLogin).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => handleEditUser(user)}
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="btn-icon btn-reset"
                        onClick={() => handleResetPassword(user._id)}
                        title="Reset Password"
                      >
                        <FaKey />
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => handleDeleteUser(user._id)}
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="user-modal-overlay" onClick={() => !submitting && setShowForm(false)}>
          <div className="user-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="user-modal-header">
              <div className="user-modal-title-section">
                <div className="user-modal-icon">
                  <FaUser />
                </div>
                <div>
                  <h2 className="user-modal-title">
                    {editingUser ? 'Edit User' : 'Add New User'}
                  </h2>
                  <p className="user-modal-subtitle">
                    {editingUser
                      ? 'Update user information and permissions'
                      : 'Create a new admin user account'}
                  </p>
                </div>
              </div>
              <button
                className="user-modal-close"
                onClick={() => !submitting && setShowForm(false)}
                disabled={submitting}
                type="button"
              >
                <FaTimes />
              </button>
            </div>

            {/* Alert Messages */}
            {formError && (
              <div className="user-alert user-alert-error">
                <FaExclamationCircle className="user-alert-icon" />
                <div className="user-alert-content">
                  <p className="user-alert-title">Error</p>
                  <p className="user-alert-description">{formError}</p>
                </div>
              </div>
            )}

            {formSuccess && (
              <div className="user-alert user-alert-success">
                <FaCheckCircle className="user-alert-icon" />
                <div className="user-alert-content">
                  <p className="user-alert-title">Success</p>
                  <p className="user-alert-description">{formSuccess}</p>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="user-form">
              <div className="user-form-grid">
                {/* Name Field */}
                <div className="user-form-group">
                  <label htmlFor="name" className="user-form-label">
                    Name <span className="user-form-required">*</span>
                  </label>
                  <div className="user-input-wrapper">
                    <div className="user-input-icon">
                      <FaUser />
                    </div>
                    <input
                      id="name"
                      type="text"
                      className="user-input"
                      placeholder="Enter full name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      disabled={submitting}
                    />
                  </div>
                </div>

                {/* Email Field */}
                <div className="user-form-group">
                  <label htmlFor="email" className="user-form-label">
                    Email <span className="user-form-required">*</span>
                  </label>
                  <div className="user-input-wrapper">
                    <div className="user-input-icon">
                      <FaEnvelope />
                    </div>
                    <input
                      id="email"
                      type="email"
                      className="user-input"
                      placeholder="name@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      disabled={submitting || editingUser}
                    />
                  </div>
                  {editingUser && (
                    <p className="user-form-hint">Email cannot be changed</p>
                  )}
                </div>

                {/* Password Field - Only for new users */}
                {!editingUser && (
                  <div className="user-form-group">
                    <label htmlFor="password" className="user-form-label">
                      Password <span className="user-form-required">*</span>
                    </label>
                    <div className="user-input-wrapper">
                      <div className="user-input-icon">
                        <FaLock />
                      </div>
                      <input
                        id="password"
                        type="password"
                        className="user-input"
                        placeholder="Minimum 6 characters"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        minLength={6}
                        disabled={submitting}
                      />
                    </div>
                    <p className="user-form-hint">Must be at least 6 characters long</p>
                  </div>
                )}

                {/* Role Field */}
                <div className="user-form-group">
                  <label htmlFor="role" className="user-form-label">
                    Role <span className="user-form-required">*</span>
                  </label>
                  <div className="user-input-wrapper">
                    <div className="user-input-icon">
                      <FaShieldAlt />
                    </div>
                    <select
                      id="role"
                      className="user-select"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      disabled={submitting}
                    >
                      <option value="admin">Admin</option>
                      <option value="super-admin">Super Admin</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Active Status Toggle */}
              <div className="user-form-group">
                <div className="user-checkbox-wrapper">
                  <input
                    id="isActive"
                    type="checkbox"
                    className="user-checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    disabled={submitting}
                  />
                  <label htmlFor="isActive" className="user-checkbox-label">
                    <span className="user-checkbox-title">Active Account</span>
                    <span className="user-checkbox-description">
                      User can login and access the system
                    </span>
                  </label>
                </div>
              </div>

              {/* Form Actions */}
              <div className="user-form-actions">
                <button
                  type="button"
                  className="user-btn user-btn-secondary"
                  onClick={() => setShowForm(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="user-btn user-btn-primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <div className="user-spinner" />
                      <span>{editingUser ? 'Updating...' : 'Creating...'}</span>
                    </>
                  ) : (
                    editingUser ? 'Update User' : 'Create User'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
